import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BuildStatusBadge } from "./BuildStatusBadge";
import { PlatformIcon } from "./PlatformIcon";
import type { Build } from "../../repositories/buildRepository";
import type { DeployJob } from "../../repositories/deployJobRepository";
import type { Storefront } from "../../repositories/storefrontRepository";

interface BuildListProps {
  builds: Build[];
  deployJobs?: DeployJob[];
  storefronts?: Storefront[];
}

function formatDuration(start: Date | null, end: Date | null): string {
  if (!start) return "--:--";
  const endTime = end ? new Date(end).getTime() : Date.now();
  const startTime = new Date(start).getTime();
  const diffMs = endTime - startTime;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s`;
  const mins = Math.floor(diffSecs / 60);
  const secs = diffSecs % 60;
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
}

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function TriggerDisplay({ trigger }: { trigger: string }) {
  const config: Record<string, { icon: string; label: string; color: string }> = {
    manual: { icon: "person", label: "Manual", color: "bg-indigo-500" },
    github_push: { icon: "commit", label: "Push", color: "bg-emerald-500" },
    github_tag: { icon: "sell", label: "Tag", color: "bg-purple-500" },
    addon: { icon: "extension", label: "Addon", color: "bg-pink-500" },
    scheduled: { icon: "schedule", label: "Scheduled", color: "bg-purple-500" },
  };

  const { icon, label, color } = config[trigger] || { icon: "help", label: trigger, color: "bg-gray-500" };

  return (
    <div className="flex items-center gap-2">
      <div className={`h-6 w-6 rounded-full ${color} flex items-center justify-center text-white`}>
        <span className="material-symbols-outlined text-[14px]">{icon}</span>
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
}

function DeployStatusDisplay({ deployJobs, storefronts }: { deployJobs: DeployJob[]; storefronts: Storefront[] }) {
  if (deployJobs.length === 0) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  // Get the most recent deploy job
  const latestJob = deployJobs[0];
  if (!latestJob) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  const storefront = storefronts.find(s => s.id === latestJob.storefrontId);

  const statusConfig: Record<string, { icon: string; color: string }> = {
    pending: { icon: "hourglass_empty", color: "text-amber-500" },
    running: { icon: "sync", color: "text-blue-500" },
    completed: { icon: "check_circle", color: "text-green-500" },
    failed: { icon: "error", color: "text-red-500" },
  };

  const { icon, color } = statusConfig[latestJob.status] || { icon: "help", color: "text-gray-500" };

  const storeName = storefront?.store === "steam" ? "Steam" : storefront?.store || "Unknown";

  return (
    <div className="flex items-center gap-1.5">
      <span className={`material-symbols-outlined text-[16px] ${color} ${latestJob.status === "running" ? "animate-spin" : ""}`}>
        {icon}
      </span>
      <span className="text-sm">
        {storeName}
        {deployJobs.length > 1 && (
          <span className="text-muted-foreground ml-1">+{deployJobs.length - 1}</span>
        )}
      </span>
    </div>
  );
}

export function BuildList({ builds, deployJobs = [], storefronts = [] }: BuildListProps) {
  const navigate = useNavigate();
  const [, setTick] = useState(0);

  // Update timer every second for in-progress builds
  const hasInProgressBuilds = builds.some(
    (build) => build.startedAt && !build.completedAt
  );

  useEffect(() => {
    if (!hasInProgressBuilds) return;

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [hasInProgressBuilds]);

  // Create a map of build ID to deploy jobs for quick lookup
  const deployJobsByBuildId = new Map<string, DeployJob[]>();
  for (const job of deployJobs) {
    const existing = deployJobsByBuildId.get(job.buildId) || [];
    existing.push(job);
    deployJobsByBuildId.set(job.buildId, existing);
  }

  if (builds.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <span className="material-symbols-outlined text-4xl mb-2 block opacity-50">construction</span>
        No builds yet. Trigger a build to get started.
      </div>
    );
  }

  const handleRowClick = (build: Build) => {
    navigate(`/projects/${build.projectId}/builds/${build.id}`);
  };

  return (
    <div className="bg-card shadow-sm rounded-xl overflow-hidden border border-border">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Build #
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Branch / Commit
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Target
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Trigger
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Deploy
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {builds.map((build) => {
              const buildDeployJobs = deployJobsByBuildId.get(build.id) || [];
              return (
                <tr
                  key={build.id}
                  className="hover:bg-muted/30 transition-colors cursor-pointer group"
                  onClick={() => handleRowClick(build)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-primary font-bold hover:underline">#{build.buildNumber}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <BuildStatusBadge status={build.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      {build.gitRef && (
                        <div className="flex items-center text-sm font-medium">
                          <span className="material-symbols-outlined text-[16px] mr-1 text-muted-foreground">
                            call_split
                          </span>
                          {build.gitRef}
                        </div>
                      )}
                      {build.gitCommit && (
                        <div className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-1">
                          <span className="material-symbols-outlined text-[14px]">commit</span>
                          {build.gitCommit.substring(0, 7)}
                        </div>
                      )}
                      {!build.gitRef && !build.gitCommit && (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <PlatformIcon platform={build.platform} size="sm" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <TriggerDisplay trigger={build.trigger} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <DeployStatusDisplay deployJobs={buildDeployJobs} storefronts={storefronts} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">
                    {formatDuration(build.startedAt, build.completedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-muted-foreground">
                    {formatRelativeDate(build.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
